/* Flav cart sync + sticky ATC guard + Appstle subscription frequency guard.
   Loaded site-wide (deferred) from snippets/scripts.liquid — wraps fetch/XHR for
   cart mutations, so it must execute before any cart request can fire. */
(function () {
  var syncTimeout = 0;
  var syncInFlight = null;
  var syncQueued = false;
  var syncAnimate = false;
  var addWatchToken = 0;
  var stickyActivationTimestamp = 0;
  var stickyRequestGuard = null;
  var stickyDrawerSuppressedUntil = 0;
  var stickyDrawerResetTimeout = 0;
  var stickyAddRequestInFlight = false;
  var stickyNativeSubmitSuppressedUntil = 0;
  var nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;
  var subscriptionFrequencyGuardTimeout = 0;
  var subscriptionFrequencyGuardForceRefresh = false;
  var subscriptionFrequencyGuardInFlight = null;
  var subscriptionFrequencyGuardCart = null;
  var subscriptionFrequencyGuardHasConflict = false;

  function getPathname(input) {
    try {
      if (input instanceof Request) {
        return new URL(input.url, window.location.origin).pathname;
      }

      return new URL(String(input), window.location.origin).pathname;
    } catch (error) {
      return '';
    }
  }

  function isCartMutationRequest(input, method) {
    var pathname = getPathname(input);
    var normalizedMethod = String(method || 'GET').toUpperCase();

    if (!pathname || normalizedMethod === 'GET') return false;

    return /^\/cart\/(add|change|update|clear)(\.js)?$/.test(pathname);
  }

  function getKnownCartCount() {
    var bubbleCount = document.querySelector('.header-actions__cart-icon .cart-bubble__text-count');
    var parsedCount = parseInt((bubbleCount && bubbleCount.textContent ? bubbleCount.textContent : '').trim(), 10);

    if (!Number.isNaN(parsedCount)) return parsedCount;

    try {
      var stored = sessionStorage.getItem('cart-count');
      if (!stored) return 0;

      var parsed = JSON.parse(stored);
      parsedCount = parseInt(parsed && parsed.value ? parsed.value : '0', 10);
      return Number.isNaN(parsedCount) ? 0 : parsedCount;
    } catch (error) {
      return 0;
    }
  }

  function getCartItemCount(cart) {
    return parseInt(String(cart && cart.item_count ? cart.item_count : 0), 10) || 0;
  }

  function getSubscriptionFrequencyDays(label) {
    var match = String(label || '').match(/(\d+)\s*day/i);
    return match ? match[1] : '';
  }

  function getCartSubscriptionFrequencies(cart) {
    var frequencies = new Set();

    ((cart && Array.isArray(cart.items)) ? cart.items : []).forEach(function (item) {
      var sellingPlanName = item
        && item.selling_plan_allocation
        && item.selling_plan_allocation.selling_plan
        && item.selling_plan_allocation.selling_plan.name;
      var frequencyDays = getSubscriptionFrequencyDays(sellingPlanName);

      if (frequencyDays) {
        frequencies.add(frequencyDays);
      }
    });

    return Array.from(frequencies).sort(function (left, right) {
      return parseInt(left, 10) - parseInt(right, 10);
    });
  }

  function getCheckoutGuardTargets() {
    return document.querySelectorAll(
      '.cart__checkout-button,' +
      ' button[name="checkout"],' +
      ' input[name="checkout"],' +
      ' form[action*="/checkout"] button,' +
      ' form[action*="/checkout"] input[type="submit"],' +
      ' a[href*="/checkout"],' +
      ' button[id*="checkout" i],' +
      ' button[class*="checkout" i],' +
      ' [data-testid="checkout-button"]'
    );
  }

  function setCheckoutGuardState(hasConflict) {
    subscriptionFrequencyGuardHasConflict = hasConflict;

    getCheckoutGuardTargets().forEach(function (target) {
      if (!(target instanceof HTMLElement)) return;

      if (!target.dataset.subscriptionGuardOriginalAriaDisabled) {
        target.dataset.subscriptionGuardOriginalAriaDisabled = target.getAttribute('aria-disabled') || '';
      }

      if (target instanceof HTMLButtonElement || target instanceof HTMLInputElement) {
        if (!target.dataset.subscriptionGuardOriginalDisabled) {
          target.dataset.subscriptionGuardOriginalDisabled = target.disabled ? 'true' : 'false';
        }

        target.disabled = hasConflict || target.dataset.subscriptionGuardOriginalDisabled === 'true';
      }

      target.setAttribute(
        'aria-disabled',
        hasConflict
          ? 'true'
          : (target.dataset.subscriptionGuardOriginalAriaDisabled || 'false')
      );
      target.toggleAttribute('data-subscription-guard-disabled', hasConflict);
    });

    document.querySelectorAll('.additional-checkout-buttons').forEach(function (container) {
      container.hidden = hasConflict;
    });
  }

  function renderSubscriptionFrequencyGuard(cart) {
    var frequencies = getCartSubscriptionFrequencies(cart);
    var hasConflict = frequencies.length > 1;
    var frequencyList = frequencies.map(function (days) {
      return days + '-day';
    }).join(', ');
    var warningText = hasConflict
      ? 'Your subscription items use multiple delivery frequencies (' + frequencyList + '). Appstle only supports one subscription frequency per checkout. Please remove and re-add one subscription item so all subscription items match before checking out.'
      : '';

    document.querySelectorAll('[data-cart-frequency-warning]').forEach(function (warning) {
      warning.textContent = warningText;
      warning.hidden = !hasConflict;
    });

    setCheckoutGuardState(hasConflict);
  }

  function fetchCartForSubscriptionFrequencyGuard(forceRefresh) {
    if (!nativeFetch) {
      return Promise.resolve(subscriptionFrequencyGuardCart);
    }

    if (!forceRefresh && subscriptionFrequencyGuardCart) {
      return Promise.resolve(subscriptionFrequencyGuardCart);
    }

    if (subscriptionFrequencyGuardInFlight) {
      return subscriptionFrequencyGuardInFlight;
    }

    subscriptionFrequencyGuardInFlight = nativeFetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Subscription frequency guard cart fetch failed');
        }

        return response.json();
      })
      .then(function (cart) {
        subscriptionFrequencyGuardCart = cart;
        return cart;
      })
      .catch(function (error) {
        console.error(error);
        return subscriptionFrequencyGuardCart;
      })
      .finally(function () {
        subscriptionFrequencyGuardInFlight = null;
      });

    return subscriptionFrequencyGuardInFlight;
  }

  function syncSubscriptionFrequencyGuard(forceRefresh) {
    return fetchCartForSubscriptionFrequencyGuard(forceRefresh).then(function (cart) {
      if (!cart) return cart;
      renderSubscriptionFrequencyGuard(cart);
      return cart;
    });
  }

  function scheduleSubscriptionFrequencyGuardSync(forceRefresh) {
    subscriptionFrequencyGuardForceRefresh = subscriptionFrequencyGuardForceRefresh || !!forceRefresh;

    if (subscriptionFrequencyGuardTimeout) {
      window.clearTimeout(subscriptionFrequencyGuardTimeout);
    }

    subscriptionFrequencyGuardTimeout = window.setTimeout(function () {
      var shouldForceRefresh = subscriptionFrequencyGuardForceRefresh;

      subscriptionFrequencyGuardTimeout = 0;
      subscriptionFrequencyGuardForceRefresh = false;
      syncSubscriptionFrequencyGuard(shouldForceRefresh);
    }, 120);
  }

  function isCheckoutAction(action) {
    return /\/checkout(?:[/?#]|$)/.test(String(action || ''));
  }

  function isCheckoutElement(target) {
    if (!(target instanceof Element)) return false;

    return !!target.closest(
      '.cart__checkout-button,' +
      ' button[name="checkout"],' +
      ' input[name="checkout"],' +
      ' form[action*="/checkout"] button,' +
      ' form[action*="/checkout"] input[type="submit"],' +
      ' a[href*="/checkout"],' +
      ' button[id*="checkout" i],' +
      ' button[class*="checkout" i],' +
      ' [data-testid="checkout-button"]'
    );
  }

  function blockCheckoutForSubscriptionConflict(event) {
    if (!subscriptionFrequencyGuardHasConflict) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    document.querySelectorAll('[data-cart-frequency-warning]').forEach(function (warning) {
      warning.hidden = false;
    });
  }

  function pruneStickyRequestGuard() {
    if (stickyRequestGuard && stickyRequestGuard.expiresAt <= Date.now()) {
      stickyRequestGuard = null;
    }
  }

  function getFormAddSignature(form) {
    if (!(form instanceof HTMLFormElement)) return '';

    var formData = new FormData(form);
    var parts = [];
    formData.forEach(function (value, key) {
      if (key === 'sections' || value instanceof File) return;
      parts.push(key + ':' + String(value));
    });

    return parts.sort().join('|');
  }

  function getBodyAddSignature(body) {
    if (!body) return '';

    var parts = [];

    if (body instanceof FormData) {
      body.forEach(function (value, key) {
        if (key === 'sections' || value instanceof File) return;
        parts.push(key + ':' + String(value));
      });
    } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      body.forEach(function (value, key) {
        if (key === 'sections') return;
        parts.push(key + ':' + String(value));
      });
    } else if (typeof body === 'string') {
      var params = new URLSearchParams(body);
      params.forEach(function (value, key) {
        if (key === 'sections') return;
        parts.push(key + ':' + String(value));
      });
    } else {
      return '';
    }

    return parts.sort().join('|');
  }

  function markStickyRequestGuard(form) {
    var signature = getFormAddSignature(form);
    if (!signature) return;

    stickyRequestGuard = {
      signature: signature,
      seen: false,
      expiresAt: Date.now() + 2000
    };
  }

  function shouldBlockDuplicateStickyAdd(input, body) {
    pruneStickyRequestGuard();

    if (!stickyRequestGuard) return false;
    if (!/^\/cart\/add(\.js)?$/.test(getPathname(input))) return false;

    var signature = getBodyAddSignature(body);
    if (!signature || signature !== stickyRequestGuard.signature) return false;

    if (stickyRequestGuard.seen) {
      return true;
    }

    stickyRequestGuard.seen = true;
    return false;
  }

  function getFlavStickyTrigger(target) {
    if (!(target instanceof Element)) return null;

    return target.closest('[data-flav-sticky-cta], [data-sticky-bar-atc]');
  }

  function isFlavAddToCartReady() {
    var productPage = document.querySelector('.flav-pdp');
    if (!(productPage instanceof HTMLElement)) return true;

    return productPage.dataset.atcReady === 'true' && productPage.dataset.atcFormReady === 'true';
  }

  function isFlavMainAddTrigger(target) {
    if (!(target instanceof Element)) return false;

    return !!target.closest('.flav-pdp__add-to-cart');
  }

  function isFlavProductForm(target) {
    if (!(target instanceof HTMLFormElement)) return false;

    return target.classList.contains('flav-pdp__form') || !!target.closest('.flav-pdp');
  }

  function shouldSuppressFlavProgrammaticSubmit(form) {
    if (!isFlavProductForm(form)) return false;

    if (!isFlavAddToCartReady()) return true;

    return stickyNativeSubmitSuppressedUntil > Date.now();
  }

  function isHeaderCartTrigger(target) {
    if (!(target instanceof Element)) return false;

    return !!target.closest('[data-testid="cart-drawer-trigger"], .header-actions__action.action__cart, .header-actions__cart-icon');
  }

  function isStickyDrawerSuppressed() {
    return stickyDrawerSuppressedUntil > Date.now();
  }

  function syncStickyDrawerSuppression() {
    if (stickyDrawerResetTimeout) {
      window.clearTimeout(stickyDrawerResetTimeout);
      stickyDrawerResetTimeout = 0;
    }

    document.documentElement.classList.toggle('flav-suppress-cart-drawer', isStickyDrawerSuppressed());

    if (!isStickyDrawerSuppressed()) return;

    stickyDrawerResetTimeout = window.setTimeout(function () {
      syncStickyDrawerSuppression();
    }, Math.max(50, stickyDrawerSuppressedUntil - Date.now() + 25));
  }

  function closeThemeCartDrawer() {
    var drawerComponent = document.querySelector('cart-drawer-component');
    if (drawerComponent && typeof drawerComponent.close === 'function') {
      drawerComponent.close();
      return;
    }

    var drawerDialog = document.querySelector('cart-drawer-component dialog[open], dialog.cart-drawer__dialog[open]');
    if (!(drawerDialog instanceof HTMLDialogElement)) return;

    drawerDialog.close();
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
  }

  function suppressStickyCartDrawer(duration) {
    stickyDrawerSuppressedUntil = Date.now() + duration;
    closeThemeCartDrawer();
    syncStickyDrawerSuppression();
  }

  function allowHeaderCartDrawerOpen() {
    stickyDrawerSuppressedUntil = 0;
    syncStickyDrawerSuppression();
  }

  window.FlavCartDrawerControl = {
    suppress: function (duration) {
      suppressStickyCartDrawer(Number(duration) || 0);
    },
    allow: function () {
      allowHeaderCartDrawerOpen();
    },
    close: function () {
      closeThemeCartDrawer();
    },
    isSuppressed: function () {
      return isStickyDrawerSuppressed();
    }
  };

  function submitFlavStickyForm() {
    var form = document.querySelector('.flav-pdp .flav-pdp__form');
    if (!(form instanceof HTMLFormElement)) return;
    if (!isFlavAddToCartReady()) {
      suppressStickyCartDrawer(1500);
      return;
    }
    if (stickyAddRequestInFlight) return;

    var submitButton = form.querySelector('.flav-pdp__add-to-cart') || form.querySelector('button[type="submit"]');
    if (!(submitButton instanceof HTMLButtonElement) || submitButton.disabled) return;

    var formData = new FormData(form);
    if (!formData.get('id')) return;

    form.dataset.flavStickySubmitAt = String(Date.now());
    markStickyRequestGuard(form);
    suppressStickyCartDrawer(60000);
    stickyAddRequestInFlight = true;

    function resetStickyInFlight() {
      if (!stickyAddRequestInFlight) return;
      stickyAddRequestInFlight = false;
    }

    document.addEventListener('cart:count-sync', resetStickyInFlight, { once: true });
    window.setTimeout(function () {
      document.removeEventListener('cart:count-sync', resetStickyInFlight);
      resetStickyInFlight();
    }, 2500);

    try {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitButton);
      } else {
        submitButton.click();
      }
    } catch (error) {
      console.error(error);
      resetStickyInFlight();
    }
  }

  window.flavSubmitStickyAddToCart = submitFlavStickyForm;

  function suppressStickyTrigger(event) {
    if (!getFlavStickyTrigger(event.target)) return false;

    event.preventDefault();
    event.stopImmediatePropagation();
    return true;
  }

  function suppressFlavAddToCartWhileLoading(event) {
    if (isFlavAddToCartReady()) return false;

    var isStickyTrigger = !!getFlavStickyTrigger(event.target);
    var isMainTrigger = isFlavMainAddTrigger(event.target);
    var isFormSubmit = event.target instanceof HTMLFormElement && isFlavProductForm(event.target);

    if (!isStickyTrigger && !isMainTrigger && !isFormSubmit) return false;

    event.preventDefault();
    event.stopImmediatePropagation();
    suppressStickyCartDrawer(1500);
    return true;
  }

  function dispatchCartCountSync(itemCount) {
    document.dispatchEvent(new CustomEvent('cart:count-sync', {
      detail: {
        itemCount: itemCount
      }
    }));
  }

  function updateNativeCartBadge(itemCount, animate) {
    var cartIcon = document.querySelector('cart-icon.header-actions__cart-icon') || document.querySelector('cart-icon');
    if (cartIcon && typeof cartIcon.renderCartBubble === 'function') {
      cartIcon.renderCartBubble(itemCount, false, animate);
      return;
    }

    var icon = document.querySelector('.header-actions__cart-icon');
    var bubble = icon && icon.querySelector('.cart-bubble');
    var bubbleCount = icon && icon.querySelector('.cart-bubble__text-count');

    if (bubbleCount) {
      bubbleCount.textContent = itemCount < 100 ? String(itemCount) : '';
      bubbleCount.classList.toggle('hidden', itemCount === 0);
    }

    if (bubble) {
      bubble.classList.toggle('visually-hidden', itemCount === 0);
    }

    if (icon) {
      icon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    }
  }

  function applyCartSync(cart, animate) {
    var itemCount = getCartItemCount(cart);

    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: String(itemCount), timestamp: Date.now() }));
    } catch (error) {
      // Ignore storage failures.
    }

    updateNativeCartBadge(itemCount, animate);
    dispatchCartCountSync(itemCount);
  }

  function performCartSync(animate) {
    if (!nativeFetch) {
      return Promise.resolve({ changed: false, itemCount: getKnownCartCount() });
    }

    if (syncInFlight) {
      syncQueued = true;
      syncAnimate = syncAnimate || animate;
      return syncInFlight;
    }

    var previousCount = getKnownCartCount();

    syncInFlight = nativeFetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var itemCount = getCartItemCount(cart);
        subscriptionFrequencyGuardCart = cart;
        renderSubscriptionFrequencyGuard(cart);

        if (itemCount !== previousCount) {
          applyCartSync(cart, animate);
        }

        return {
          changed: itemCount !== previousCount,
          itemCount: itemCount
        };
      })
      .catch(function () {
        return {
          changed: false,
          itemCount: previousCount
        };
      })
      .finally(function () {
        syncInFlight = null;

        if (syncQueued) {
          var shouldAnimate = syncAnimate;
          syncQueued = false;
          syncAnimate = false;
          scheduleCartSync(shouldAnimate);
        }
      });

    return syncInFlight;
  }

  function scheduleCartSync(animate) {
    if (syncTimeout) {
      window.clearTimeout(syncTimeout);
    }

    syncAnimate = syncAnimate || animate;

    syncTimeout = window.setTimeout(function () {
      var shouldAnimate = syncAnimate;
      syncTimeout = 0;
      syncAnimate = false;
      performCartSync(shouldAnimate);
    }, 180);
  }

  function watchForCartChange() {
    var token = ++addWatchToken;
    var attemptsRemaining = 12;

    function attempt(delay) {
      window.setTimeout(function () {
        if (token !== addWatchToken) return;

        performCartSync(true).then(function (result) {
          if (token !== addWatchToken) return;
          if (result.changed || attemptsRemaining <= 1) return;

          attemptsRemaining -= 1;
          attempt(350);
        });
      }, delay);
    }

    attempt(250);
  }

  function isAddToCartForm(target) {
    return target instanceof HTMLFormElement
      && (target.matches('form[data-type="add-to-cart-form"]') || /\/cart\/add/.test(target.getAttribute('action') || ''));
  }

  function isAddToCartButton(target) {
    if (!(target instanceof Element)) return false;

    return !!target.closest(
      'button[name="add"], .add-to-cart-button, .flav-pdp__add-to-cart'
    );
  }

  function activateStickyTrigger(event) {
    if (!suppressStickyTrigger(event)) return;

    var now = Date.now();
    if (now - stickyActivationTimestamp < 500) return;

    stickyActivationTimestamp = now;
    submitFlavStickyForm();
  }

  function handleHeaderCartIntent(event) {
    if (!isHeaderCartTrigger(event.target)) return;

    allowHeaderCartDrawerOpen();
  }

  if (typeof HTMLDialogElement !== 'undefined') {
    var originalShow = HTMLDialogElement.prototype.show;
    var originalShowModal = HTMLDialogElement.prototype.showModal;

    function isCartDrawerDialog(dialog) {
      if (!(dialog instanceof HTMLDialogElement)) return false;

      if (dialog.closest('cart-drawer-component') || dialog.classList.contains('cart-drawer__dialog')) {
        return true;
      }

      return false;
    }

    HTMLDialogElement.prototype.show = function () {
      if (isStickyDrawerSuppressed() && isCartDrawerDialog(this)) return;
      return originalShow.apply(this, arguments);
    };

    HTMLDialogElement.prototype.showModal = function () {
      if (isStickyDrawerSuppressed() && isCartDrawerDialog(this)) return;
      return originalShowModal.apply(this, arguments);
    };
  }

  if (typeof HTMLFormElement !== 'undefined') {
    var originalFormSubmit = HTMLFormElement.prototype.submit;
    var originalFormRequestSubmit = HTMLFormElement.prototype.requestSubmit;

    HTMLFormElement.prototype.submit = function () {
      if (shouldSuppressFlavProgrammaticSubmit(this)) return;
      return originalFormSubmit.apply(this, arguments);
    };

    if (typeof originalFormRequestSubmit === 'function') {
      HTMLFormElement.prototype.requestSubmit = function () {
        if (shouldSuppressFlavProgrammaticSubmit(this)) return;
        return originalFormRequestSubmit.apply(this, arguments);
      };
    }
  }

  window.addEventListener('touchstart', suppressStickyTrigger, { capture: true, passive: false });
  window.addEventListener('touchend', activateStickyTrigger, { capture: true, passive: false });
  window.addEventListener('pointerdown', suppressStickyTrigger, { capture: true, passive: false });
  window.addEventListener('pointerup', activateStickyTrigger, { capture: true, passive: false });
  window.addEventListener('mousedown', suppressStickyTrigger, true);
  window.addEventListener('mouseup', activateStickyTrigger, true);
  window.addEventListener('click', handleHeaderCartIntent, true);
  window.addEventListener('touchend', handleHeaderCartIntent, { capture: true, passive: true });
  window.addEventListener('pointerup', handleHeaderCartIntent, { capture: true, passive: true });
  window.addEventListener('click', suppressFlavAddToCartWhileLoading, true);
  window.addEventListener('submit', suppressFlavAddToCartWhileLoading, true);
  window.addEventListener('click', suppressStickyTrigger, true);
  window.addEventListener('pageshow', function () {
    scheduleSubscriptionFrequencyGuardSync(true);
  });
  document.addEventListener('cart:update', function () {
    scheduleSubscriptionFrequencyGuardSync(true);
  });

  if (nativeFetch) {
    window.fetch = function (input, init) {
      var method = input instanceof Request ? input.method : init && init.method;
      var shouldSyncCart = isCartMutationRequest(input, method);
      var requestBody = init && init.body;

      if (shouldBlockDuplicateStickyAdd(input, requestBody)) {
        return Promise.reject(new Error('Blocked duplicate sticky add-to-cart request'));
      }

      return nativeFetch(input, init).then(function (response) {
        if (shouldSyncCart && response && response.ok) {
          scheduleCartSync(true);
        }

        return response;
      });
    };
  }

  if (window.XMLHttpRequest) {
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__cartSyncMethod = method;
      this.__cartSyncUrl = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (shouldBlockDuplicateStickyAdd(this.__cartSyncUrl, arguments[0])) {
        window.setTimeout(() => {
          this.dispatchEvent(new ProgressEvent('error'));
          this.dispatchEvent(new ProgressEvent('loadend'));
        }, 0);
        return;
      }

      if (isCartMutationRequest(this.__cartSyncUrl, this.__cartSyncMethod)) {
        this.addEventListener('loadend', function () {
          if (this.status >= 200 && this.status < 300) {
            scheduleCartSync(true);
          }
        });
      }

      return originalSend.apply(this, arguments);
    };
  }

  document.addEventListener('submit', function (event) {
    if (isAddToCartForm(event.target)) {
      watchForCartChange();
    }
  }, true);

  document.addEventListener('click', function (event) {
    if (isAddToCartButton(event.target)) {
      watchForCartChange();
    }
  }, true);

  document.addEventListener('click', function (event) {
    if (!isCheckoutElement(event.target)) return;
    blockCheckoutForSubscriptionConflict(event);
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    var submitter = event.submitter;
    if (!isCheckoutAction(form.getAttribute('action')) && !isCheckoutElement(submitter)) return;

    blockCheckoutForSubscriptionConflict(event);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scheduleSubscriptionFrequencyGuardSync(true);
    }, { once: true });
  } else {
    scheduleSubscriptionFrequencyGuardSync(true);
  }
})();
